import React, { useContext, useState } from "react";
import "../../styles/quizCard.css";
import { WebsiteContext } from "../../context/AppContext";
import ContentComponent from "../common/ContentComponent";
import quizImg from "../../images/quiz.webp";
import { HiFire } from "react-icons/hi2";
import { LuRefreshCw } from "react-icons/lu";
import { IoCheckmarkCircle, IoInformationCircle } from "react-icons/io5";
import { RiBookReadLine, RiPencilRulerLine } from "react-icons/ri";
import { motion, AnimatePresence } from "framer-motion";

const quizData = {
  vocab: [
    {
      category: "Vocabulary",
      question: "Which word is the closest synonym for 'Meticulous'?",
      options: [
        { label: "Careless and hasty", isCorrect: false },
        { label: "Showing great attention to detail", isCorrect: true },
        { label: "Unusually loud and boisterous", isCorrect: false },
        { label: "Uncertain about consequences", isCorrect: false },
      ],
      explanation: "'Meticulous' means very careful and precise in considering every small detail.",
    },
    {
      category: "Vocabulary",
      question: "What is the antonym of the word 'Obsolete'?",
      options: [
        { label: "Archaic", isCorrect: false },
        { label: "Contemporary", isCorrect: true },
        { label: "Outdated", isCorrect: false },
        { label: "Vanished", isCorrect: false },
      ],
      explanation: "'Obsolete' means no longer produced or used; 'Contemporary' or 'Modern' is the opposite.",
    },
  ],
  grammar: [
    {
      category: "Grammar",
      question: "Choose the grammatically correct sentence:",
      options: [
        { label: "Neither the teacher nor the students was present.", isCorrect: false },
        { label: "Neither the teacher nor the students were present.", isCorrect: true },
        { label: "Neither the teacher or the students were present.", isCorrect: false },
        { label: "Neither of students were present.", isCorrect: false },
      ],
      explanation: "With 'neither... nor', the verb agrees with the subject closest to it ('the students were').",
    },
    {
      category: "Grammar",
      question: "Identify the correct preposition: 'She has lived here _____ five years.'",
      options: [
        { label: "since", isCorrect: false },
        { label: "for", isCorrect: true },
        { label: "from", isCorrect: false },
        { label: "during", isCorrect: false },
      ],
      explanation: "Use 'for' with a duration of time ('five years'), and 'since' with a specific starting point.",
    },
  ],
};

const QuizCardSection = () => {
  const { isMobileView, dark } = useContext(WebsiteContext);
  const [activeTab, setActiveTab] = useState("vocab");
  const [questionIndex, setQuestionIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);

  const currentQuestions = quizData[activeTab];
  const current = currentQuestions[questionIndex % currentQuestions.length];

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setQuestionIndex(0);
    setSelectedOption(null);
  };

  const handleNext = () => {
    setQuestionIndex((prev) => (prev + 1) % currentQuestions.length);
    setSelectedOption(null);
  };

  const handleSelectOption = (index) => {
    if (selectedOption !== null) return; // already answered
    setSelectedOption(index);
  };

  const optionLetters = ["A", "B", "C", "D"];

  return (
    <div className={isMobileView ? "quiz_showcase_section_mobile" : "quiz_showcase_section"}>
      {/* On desktop, card on the left for alternating balance */}
      <div className="quiz_card_wrapper">
        <img src={quizImg} alt="3D Quiz Icon" className="quiz_3d_badge" />

        <div className={`quiz_card ${dark ? "quiz_card_dark" : "quiz_card_light"}`}>
          {/* Tabs for Vocab vs Grammar */}
          <div className="quiz_tabs">
            <button
              className={`quiz_tab_btn ${activeTab === "vocab" ? "quiz_tab_btn_active" : ""}`}
              onClick={() => handleTabChange("vocab")}
            >
              <RiBookReadLine size={15} /> Vocab Quiz
            </button>
            <button
              className={`quiz_tab_btn ${activeTab === "grammar" ? "quiz_tab_btn_active" : ""}`}
              onClick={() => handleTabChange("grammar")}
            >
              <RiPencilRulerLine size={15} /> Grammar Quiz
            </button>
          </div>

          {/* Meta Bar */}
          <div className="quiz_meta_bar">
            <span className="quiz_badge">
              <IoCheckmarkCircle size={13} /> {current.category} Challenge
            </span>
            <span className="quiz_streak">
              <HiFire size={15} /> 5 Day Streak
            </span>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={`${activeTab}-${questionIndex}`}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.25 }}
            >
              {/* Question */}
              <div className="quiz_question_box">
                <div className="quiz_question_prompt">Question {(questionIndex % currentQuestions.length) + 1} of 10</div>
                <div className="quiz_question_text">{current.question}</div>
              </div>

              {/* Options */}
              <div className="quiz_options_list">
                {current.options.map((opt, idx) => {
                  let statusClass = "";
                  if (selectedOption !== null) {
                    if (opt.isCorrect) {
                      statusClass = "quiz_option_correct";
                    } else if (selectedOption === idx) {
                      statusClass = "quiz_option_wrong";
                    }
                  }

                  return (
                    <button
                      key={idx}
                      className={`quiz_option_btn ${statusClass}`}
                      onClick={() => handleSelectOption(idx)}
                    >
                      <span className="quiz_option_letter">{optionLetters[idx]}</span>
                      <span>{opt.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Explanation on Answer */}
              {selectedOption !== null && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="quiz_explanation_box"
                >
                  <IoInformationCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <strong>Explanation: </strong> {current.explanation}
                  </div>
                </motion.div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Footer Bar */}
          <div className="quiz_footer_bar">
            <span className="quiz_hint">Tap an option to test yourself</span>
            <button className="quiz_cycle_btn" onClick={handleNext} title="Next question">
              <LuRefreshCw size={12} /> Next Question
            </button>
          </div>
        </div>
      </div>

      {/* Description on the side */}
      <div className="quiz_text_wrapper">
        <ContentComponent
          title={"Interactive Quizzes: Vocabulary & Grammar Mastery"}
          content={
            "Reinforce what you learn with targeted daily quizzes. Test subtle grammar rules and elevate word recall through bite-sized challenges with instant explanations designed to make rules stick."
          }
        />
      </div>
    </div>
  );
};

export default QuizCardSection;
